import React, { useState, useEffect, useRef } from 'react'
import { RiSurveyLine } from 'react-icons/ri'
import { FiAlertCircle } from 'react-icons/fi'
import styled from 'styled-components'
import {
  Wrapper, Header, Body, Footer,
  Title, Sub, NavButton
} from '../surveyStyles'

import ShortTextInput from './Surveys/ShortTextInput'
import LongTextInput from './Surveys/LongTextInput'
import YesNoInput from './Surveys/YesNoInput'
import NumberInput from './Surveys/NumberInput'
import DateInput from './Surveys/DateInput'
import EmojiInput from './Surveys/EmojiInput'
import SingleChoiceInput from './Surveys/SingleChoiceInput'
import MultiChoiceInput from './Surveys/MultiChoiceInput'
import ScaleInput from './Surveys/ScaleInput'

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  color: #dc2626;
  font-size: 14px;
  > svg {
    margin-right: 4px;
  }
`

const QuestionGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`

export const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
`

export const SUBMITTED_MESSAGE = 'Pesquisa enviada com sucesso.'
const NEXT_CONTENT_LABEL = 'Próximo conteúdo'
const REQUIRED_LABEL = ' (obrigatória)'
const REQUIRED_ERROR_MESSAGE = 'Esta pergunta é obrigatória.'
const THANK_YOU_TYPE = 'thankyou'

export default function SurveyRenderer({
  survey,
  onSubmit,
  forceThankYou = false,
  nextNavigationContent,
  isOnboarding = false,
  isCourseContent = false
}) {
  const questions = Array.isArray(survey.questions) ? survey.questions : []
  const pages = [
    ...questions,
    {
      id: 'thankyou',
      typeId: 'thankyou',
      title: survey.thankYouPage.title,
      subtitle: survey.thankYouPage.subtitle
    }
  ]

  const [idx, setIdx] = useState(forceThankYou ? pages.length - 1 : 0)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const titleRef = useRef(null)
  const hasMounted = useRef(false)

  const page = pages[idx]
  const isFirst = idx === 0
  const isLastQuestion = idx === pages.length - 2 // última pergunta antes da thankyou

  const setAnswer = (id, val) => {
    setAnswers(a => ({ ...a, [id]: val }))
    setErrors(e => {
      const ne = { ...e }
      delete ne[id]
      return ne
    })
  }

  const renderInput = q => {
    switch (q.typeId) {
      case 'instruction': return null
      case 'shortText': return <ShortTextInput value={answers[q.id] || ''} max={q.maxCharsEnabled ? q.maxChars : undefined} onChange={val => setAnswer(q.id, val)} />
      case 'longText': return <LongTextInput value={answers[q.id] || ''} max={q.maxCharsEnabled ? q.maxChars : undefined} onChange={val => setAnswer(q.id, val)} />
      case 'yesNo': return <YesNoInput value={answers[q.id]} onChange={val => setAnswer(q.id, val)} />
      case 'number': return <NumberInput value={answers[q.id] || ''} format={q.numberFormat || 'free'} onChange={val => setAnswer(q.id, val)} />
      case 'date': return <DateInput value={answers[q.id] || ''} dateFormat={q.dateFormat} onChange={val => setAnswer(q.id, val)} />
      case 'emoji': return <EmojiInput options={q.options || []} value={answers[q.id] || 0} onChange={val => setAnswer(q.id, val)} />
      case 'singleChoice': return <SingleChoiceInput options={q.options || []} otherEnabled={q.otherEnabled} value={answers[q.id]} onChange={label => setAnswer(q.id, label)} />
      case 'multiChoice': return <MultiChoiceInput options={q.options || []} value={answers[q.id] || []} onChange={val => setAnswer(q.id, val)} otherEnabled={q.otherEnabled} />
      case 'scale': return <ScaleInput question={q} value={answers[q.id] || q.scaleMin || 0} onChange={val => setAnswer(q.id, val)} />
      default: return null
    }
  }

  useEffect(() => {
    const shouldFocus = hasMounted.current || isCourseContent
    hasMounted.current = true
    if (shouldFocus && titleRef.current) titleRef.current.focus()
  }, [idx])

  const submitAnswers = () => {
    if (submitted || forceThankYou) return
    setSubmitted(true)
    onSubmit(
      Object.entries(answers).map(([id, ans]) => ({
        questionId: Number(id),
        answer: ans
      }))
    )
  }

  const next = () => {
    if (page.typeId !== 'instruction' && 
        page.required &&
        (answers[page.id] === undefined || answers[page.id] === '' ||
          (Array.isArray(answers[page.id]) && answers[page.id].length === 0))
    ) {
      setErrors(e => ({ ...e, [page.id]: REQUIRED_ERROR_MESSAGE }))
      return
    }

    if (isLastQuestion) {
      submitAnswers()
    }

    setIdx(i => i + 1)
  }

  const finish = () => {
    if (!isOnboarding) {
      submitAnswers()
    }

    if (forceThankYou && nextNavigationContent) {
      nextNavigationContent()
    }
  }

  const renderThankYouAction = () => {
    if (isCourseContent) {
      return nextNavigationContent && (
        <NavButton onClick={nextNavigationContent}>{NEXT_CONTENT_LABEL}</NavButton>
      )
    }
    return survey.thankYouPage.ctaEnabled && !isOnboarding && (
      <NavButton onClick={finish}>
        {survey.thankYouPage.ctaText || 'Fechar'}
      </NavButton>
    )
  }

  return (
    <Wrapper isCourseContent={!!isCourseContent} isOnboarding={!!isOnboarding}>
      {!isOnboarding && (
      <Header>
        <RiSurveyLine size={24} /> {survey.title || survey.name}
      </Header>)}
      <Body>
        {page.typeId === THANK_YOU_TYPE && (
              <img
                src="/images/icons/thanks.svg" 
                alt="Pesquisa concluída"
                style={{ width: 64, height: 64 }}
              />
            )}
        <Title id="survey-page-title" tabIndex={-1} innerRef={titleRef}>
          {page.title}
          {isCourseContent && page.required && page.typeId !== 'instruction' && REQUIRED_LABEL}
        </Title>
        {page.subtitle && <Sub>{page.subtitle}</Sub>}

        {page.typeId === THANK_YOU_TYPE
          ? renderThankYouAction()
          : (
              <QuestionGroup
                role="group"
                aria-labelledby="survey-page-title"
                aria-describedby={errors[page.id] ? 'survey-page-error' : undefined}
              >
                {renderInput(page)}
                {errors[page.id] && (
                  <ErrorMessage id="survey-page-error" role="alert">
                    <FiAlertCircle size={16} />
                    {errors[page.id]}
                  </ErrorMessage>
                )}
              </QuestionGroup>
            )}
      </Body>
      <VisuallyHidden role="status" aria-live="polite">
        {!isCourseContent && submitted && !forceThankYou && page.typeId === THANK_YOU_TYPE && SUBMITTED_MESSAGE}
      </VisuallyHidden>

      <Footer>
        <NavButton
          disabled={isFirst || forceThankYou}
          onClick={() => setIdx(i => i - 1)}
        >
          &lt; Pergunta Anterior
        </NavButton>

        <span>{idx + 1}/{pages.length}</span>

        {page.typeId !== 'thankyou' && (
          <NavButton onClick={next}>
            {isLastQuestion ? 'Avançar' : 'Próxima Pergunta >'}
          </NavButton>
        )}
      </Footer>
    </Wrapper>
  )
}

